<template>
  <div class="country-select" :class="{ 'country-select--open': isOpen }">
    <button
      class="country-select__trigger"
      type="button"
      :aria-expanded="String(isOpen)"
      aria-haspopup="listbox"
      @click.stop="toggle"
    >
      <span class="country-select__code">{{
        selectedCountry ? selectedCountry.label : placeholder
      }}</span>
      <img
        v-if="selectedCountry"
        class="country-select__flag"
        :src="selectedCountry.flag"
        :alt="selectedCountry.label"
      />
      <img
        class="country-select__arrow"
        :class="{ 'country-select__arrow--open': isOpen }"
        src="@/assets/icons/icon-arrow.svg"
        alt=""
      />
    </button>
    <ul v-show="isOpen" class="country-select__dropdown" role="listbox">
      <li v-for="item in options" :key="item.value">
        <button
          class="country-select__option"
          :class="{ 'country-select__option--active': item.value === value }"
          type="button"
          role="option"
          :aria-selected="item.value === value"
          @click.stop="choose(item)"
        >
          <span>{{ item.label }}</span>
          <img class="country-select__flag" :src="item.flag" :alt="item.label" />
        </button>
      </li>
    </ul>
  </div>
</template>

<script>
export default {
  name: 'CountrySelect',
  props: {
    value: {
      type: String,
      default: '',
    },
    options: {
      type: Array,
      default: () => [],
    },
    placeholder: {
      type: String,
      default: 'Страна',
    },
  },
  data() {
    return {
      isOpen: false,
    };
  },
  computed: {
    selectedCountry() {
      return this.options.find((item) => item.value === this.value) || this.options[0] || null;
    },
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside, true);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside, true);
  },
  methods: {
    toggle() {
      this.isOpen = !this.isOpen;
    },
    choose(item) {
      this.isOpen = false;
      if (item.value === this.value) {
        return;
      }
      this.$emit('input', item.value);
      this.$emit('change', item.value);
    },
    handleClickOutside(e) {
      if (!this.$el.contains(e.target)) {
        this.isOpen = false;
      }
    },
  },
};
</script>

<style lang="scss">
@import '@/assets/styles/helpers/variables';
@import '@/assets/styles/helpers/mixin';

.country-select {
  position: relative;
  width: 10rem;
  flex-shrink: 0;
  z-index: 4;

  &--open {
    z-index: 20;
  }

  &__trigger {
    display: flex;
    align-items: center;
    width: 100%;
    height: 5rem;
    padding: 0 1rem 0 1.2rem;
    border: 1px solid $gray3;
    border-radius: 8px;
    background: $basicWhite;
    cursor: pointer;
    appearance: none;
    -webkit-appearance: none;
    touch-action: manipulation;
    font-family: inherit;
    font-size: 1.4rem;
    color: $gray;
    gap: 0.6rem;
  }

  &__flag {
    width: 20px;
    height: 20px;
    object-fit: cover;
    flex-shrink: 0;
    pointer-events: none;
  }

  &__code {
    flex-grow: 1;
    text-align: left;
    line-height: 1;
  }

  &__arrow {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    pointer-events: none;
    transition: transform 0.2s ease-out;

    &--open {
      transform: rotate(180deg);
    }
  }

  &__dropdown {
    position: absolute;
    top: calc(100% + 0.4rem);
    left: 0;
    min-width: 100%;
    margin: 0;
    padding: 0.4rem 0;
    list-style: none;
    background: $basicWhite;
    border: 1px solid $gray3;
    border-radius: 8px;
    box-shadow: 0 4px 15px rgba(10, 31, 55, 0.1);
    z-index: 20;
  }

  &__option {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.8rem;
    width: 100%;
    min-height: 4rem;
    padding: 0.8rem 1.2rem;
    border: none;
    background: transparent;
    cursor: pointer;
    appearance: none;
    -webkit-appearance: none;
    font-family: inherit;
    font-size: 1.4rem;
    color: $basicBlack;
    text-align: left;
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
    user-select: none;

    &--active {
      background: var(--primary);
      color: $basicWhite;
    }

    @media (hover: hover) and (pointer: fine) {
      &:hover:not(.country-select__option--active) {
        background: $gray8;
      }
    }
  }

  @include below(577px) {
    width: 10rem;

    &__trigger {
      height: 5rem;
      padding: 0 0.8rem 0 1rem;
    }

    &__option {
      min-height: 4.4rem;
    }
  }
}
</style>
